import { useEffect, useState } from 'react';

import {
  BellOutlined,
  ExportOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  CheckCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { App, Button, Statistic, Tag } from 'antd';
import type { FilterValue, TablePaginationConfig } from 'antd/es/table/interface';
import { useNavigate } from 'react-router';

import { useGetSchoolDataQuery } from '@/api/endpoints/schools';
import {
  type ITest,
  EAdmissionTestingStatus,
  type IAdmissionTesting,
  useGetTestingParticipantsQuery,
  useSendReminderLettersMutation,
  useSendDecisionsMutation,
  useSendTestResultsMutation,
  useGetAdmissionTestingQuery,
} from '@/api/endpoints/schools/schoolSelectionPanel';
import { useCreateTeachbaseQuizMutation, useGenerateTeachbaseAuthLinkMutation } from '@/api/endpoints/teachbase';
import type { IApiError } from '@/api/types';
import { ConfirmModal, CustomTable, FetchDataWrapper } from '@/shared/components';
import { EFeatureFlags, ROUTES } from '@/shared/consts';
import { checkIsFlagEnabled, generateErrorMessage } from '@/shared/helpers';
import { useApiRequest, useLoader } from '@/shared/hooks';
import type { ETestStatus } from '@/shared/types';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';
import { AttachTestModal } from '../_components/AttachTestModal/AttachTestModal';
import { ModerateParticipantModal } from '../_components/ModerateParticipantModal/ModerateParticipantModal';
import { ReservedUsersModal } from '../_components/ReservedUsersModal/ReservedUsersModal';

import { formatTestingStats, getColumnsData } from './helpers';
import styles from './TestingParticipantsPage.module.scss';

interface IProp {
  schoolId: number;
  admissionTestingData: IAdmissionTesting;
}

export const TestingParticipantsPage = ({ schoolId, admissionTestingData }: IProp) => {
  const [isAttachTestModalOpen, setIsAttachTestModalOpen] = useState(false);
  const [isModerateParticipantModalOpen, setIsModerateParticipantModalOpen] = useState(false);
  const [isSendReminderLettersModalOpen, setIsSendReminderLettersModalOpen] = useState(false);
  const [isReservedUsersModalOpen, setIsReservedUsersModalOpen] = useState(false);
  const [isSendDecisionsModalOpen, setIsSendDecisionsModalOpen] = useState(false);
  const [isSendTestResultModalOpen, setIsSendTestResultModalOpen] = useState(false);
  const [itemToOperate, setItemToOperate] = useState<ITest | null>(null);
  const [testingTableFilters, setTestingTableFilters] = useState<Record<string, FilterValue | null>>({});
  const [testingTablePagination, setTestingTablePagination] = useState<TablePaginationConfig>({
    pageSize: 10,
    current: 1,
    total: 0,
  });

  const { showLoader, hideLoader } = useLoader();
  const sendApiRequest = useApiRequest();

  const navigate = useNavigate();

  const {
    data: schoolData,
    isLoading: isSchoolLoading,
    isFetching: isSchoolFetching,
    isError: isSchoolFetchError,
  } = useGetSchoolDataQuery({ id: schoolId });
  const {
    data: testingData,
    isLoading: isTestingLoading,
    isFetching: isTestingFetching,
    isError: isTestingFetchError,
    refetch: refetchTestingParticipants,
  } = useGetTestingParticipantsQuery({
    schoolId,
    page: testingTablePagination.current,
    perPage: testingTablePagination.pageSize,
    statusFilter: testingTableFilters.status as ETestStatus[],
  });
  const { refetch: refetchAdmissionTesting } = useGetAdmissionTestingQuery({ schoolId: Number(schoolId) });
  const [createQuiz] = useCreateTeachbaseQuizMutation();
  const [generateAuthLink] = useGenerateTeachbaseAuthLinkMutation();
  const [sendReminderLetters, { isLoading: areReminderLettersSending }] = useSendReminderLettersMutation();
  const [sendDecisions, { isLoading: areDecisionsSending }] = useSendDecisionsMutation();
  const [sendTestResults, { isLoading: areTestResultsSending }] = useSendTestResultsMutation();

  const { message } = App.useApp();
  const { origin, pathname, search } = window.location;
  const { status: admissionTestingStatus } = admissionTestingData;

  const isReserveAvailable = Boolean(testingData?.meta?.stats?.in_reserve);

  const creteQuizAndRedirectHandle = async () => {
    showLoader();

    try {
      const {
        id: courseId,
        content: {
          last_version: { education_content_id: educationContentId, id: lastVersionId },
        },
      } = await createQuiz({ LMSAccountId: Number(schoolData?.lms_account_id) }).unwrap();

      const { url } = await generateAuthLink({
        redirectURL: `/manager/quizzes/course/${courseId}/content/${educationContentId}/version/${lastVersionId}/edit/content?redirect_url=${origin}${pathname}${search}`,
        LMSAccountId: Number(schoolData?.lms_account_id),
      }).unwrap();

      window.location.assign(url);
    } catch (error) {
      hideLoader();

      message.error(generateErrorMessage(error as IApiError));
    }
  };

  const formattedTestingStats = formatTestingStats(testingData?.meta.stats);

  const columns = getColumnsData({
    filters: testingTableFilters,
    openModerateModalCb: () => setIsModerateParticipantModalOpen(true),
    setItemToOperate,
  });

  const refreshTestingParticipants = () => {
    sendApiRequest({
      apiRequestCallback: () => ({
        unwrap: () => Promise.all([refetchTestingParticipants(), refetchAdmissionTesting()]),
      }),
    });
  };

  const remindToNotStarted = () => {
    sendApiRequest({
      apiRequestCallback: () => sendReminderLetters({ schoolId: Number(schoolId) }),
      successMessage: 'Уведомления успешно отправлены',
      successCallback: () => setIsSendReminderLettersModalOpen(false),
    });
  };

  const checkInLMS = async () => {
    try {
      showLoader();

      const { url } = await generateAuthLink({
        redirectURL: '/manager/checking-new/task',
        LMSAccountId: Number(schoolData?.lms_account_id),
      }).unwrap();

      window.location.assign(url);
    } catch (error) {
      hideLoader();

      message.error(generateErrorMessage(error as IApiError));
    }
  };

  const sendDecisionsLetters = () => {
    sendApiRequest({
      apiRequestCallback: () => sendDecisions({ schoolId: Number(schoolId) }),
      successMessage: 'Уведомления успешно отправлены',
      successCallback: () => setIsSendDecisionsModalOpen(false),
    });
  };

  const sendTestResultsCb = () => {
    sendApiRequest({
      apiRequestCallback: () => sendTestResults({ schoolId: Number(schoolId) }),
      successMessage: 'Уведомления успешно отправлены',
      successCallback: () => setIsSendTestResultModalOpen(false),
    });
  };

  useEffect(() => {
    if (testingData?.meta.pagination.total_count) {
      setTestingTablePagination((prev) => ({
        ...prev,
        total: testingData.meta.pagination.total_count,
      }));
    }
  }, [testingData?.meta.pagination.total_count]);

  useEffect(() => {
    if (isSchoolFetching || isTestingFetching) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isSchoolFetching || isTestingFetching]);

  return (
    <FetchDataWrapper
      isLoading={isSchoolLoading || isTestingLoading}
      isError={isSchoolFetchError || isTestingFetchError}
    >
      {schoolData && testingData && (
        <div className={styles.wrapper}>
          <SelectionPanelHeader currentTab={ESelectionPanelTabs.Testing} />
          <div className={styles['info-wrapper']}>
            <div className={styles['content-wrapper']}>
              <div className={styles['title-wrapper']}>
                <p className={styles.title}>Этап 2: Тестирование</p>
                <Button
                  icon={<ReloadOutlined />}
                  shape="circle"
                  size="small"
                  onClick={refreshTestingParticipants}
                />
              </div>
              <p className={styles.description}>
                Создайте тест в LMS и привяжите его к программе. Участники проходят тестирование, после проверки
                установите результат. Успешно сдавшим предложите предоставить документы, после проверки СБ одобрите или
                отклоните
              </p>
            </div>
            <div className={styles['test-buttons-wrapper']}>
              {admissionTestingStatus === EAdmissionTestingStatus.Draft ? (
                <Button
                  variant="outlined"
                  color="purple"
                  icon={<LinkOutlined />}
                  onClick={() => setIsAttachTestModalOpen(true)}
                >
                  Привязать тест
                </Button>
              ) : (
                <Tag
                  className={styles['test-attachment-tag']}
                  color="green"
                  icon={<CheckCircleOutlined />}
                  variant="outlined"
                >
                  Тест привязан
                </Tag>
              )}
              <Button
                variant="solid"
                color="purple"
                icon={<ExportOutlined />}
                onClick={creteQuizAndRedirectHandle}
                disabled={admissionTestingStatus !== EAdmissionTestingStatus.Draft}
              >
                Создать тест в LMS
              </Button>
            </div>
          </div>
          <div className={styles['statistic-wrapper']}>
            {formattedTestingStats?.map(({ id, title, value }) => (
              <Statistic
                className={styles['statistic-item']}
                key={id}
                title={title}
                value={value}
              />
            ))}
          </div>
          {admissionTestingStatus !== EAdmissionTestingStatus.Finished &&
            admissionTestingStatus !== EAdmissionTestingStatus.Draft && (
              <div className={styles['action-buttons-wrapper']}>
                {checkIsFlagEnabled(EFeatureFlags.AdmissionExamReserve) &&
                  (admissionTestingStatus === EAdmissionTestingStatus.ReadyForDecisions ||
                    admissionTestingStatus === EAdmissionTestingStatus.ResultsNotified) &&
                  isReserveAvailable && (
                    <Button
                      icon={<UserOutlined />}
                      onClick={() => setIsReservedUsersModalOpen(true)}
                      color="cyan"
                      variant="outlined"
                    >
                      Управление резервом
                    </Button>
                  )}
                {(admissionTestingStatus === EAdmissionTestingStatus.InProgress ||
                  admissionTestingStatus === EAdmissionTestingStatus.ReadyForResults) && (
                  <Button
                    icon={<SearchOutlined />}
                    onClick={checkInLMS}
                    color="purple"
                    variant="outlined"
                  >
                    Проверить в LMS
                  </Button>
                )}
                {(admissionTestingStatus === EAdmissionTestingStatus.InProgress ||
                  admissionTestingStatus === EAdmissionTestingStatus.ReadyForResults) && (
                  <Button
                    icon={<BellOutlined />}
                    onClick={() => setIsSendReminderLettersModalOpen(true)}
                    color="red"
                    variant="outlined"
                    disabled={
                      admissionTestingStatus !== EAdmissionTestingStatus.InProgress &&
                      admissionTestingStatus !== EAdmissionTestingStatus.ReadyForResults
                    }
                  >
                    Напомнить неприступившим
                  </Button>
                )}
                {admissionTestingStatus === EAdmissionTestingStatus.ReadyForResults &&
                  (checkIsFlagEnabled(EFeatureFlags.AdmissionExamReserve) ? (
                    <Button
                      icon={<SendOutlined rotate={-45} />}
                      onClick={() => navigate(ROUTES.USERS_ASSIGNMENT)}
                      color="geekblue"
                      variant="solid"
                    >
                      Распределить и уведомить
                    </Button>
                  ) : (
                    <Button
                      icon={<SendOutlined rotate={-45} />}
                      onClick={() => setIsSendTestResultModalOpen(true)}
                      color="geekblue"
                      variant="solid"
                    >
                      Уведомить о результатах теста
                    </Button>
                  ))}
                {admissionTestingStatus === EAdmissionTestingStatus.ReadyForDecisions && (
                  <Button
                    icon={<SendOutlined rotate={-45} />}
                    onClick={() => setIsSendDecisionsModalOpen(true)}
                    color="blue"
                    variant="solid"
                  >
                    Отправить финальные уведомления
                  </Button>
                )}
              </div>
            )}
          <CustomTable
            rowKey="id"
            pagination={{ ...testingTablePagination }}
            dataSource={testingData.data}
            onChange={(pagination, filters, _, extra) => {
              setTestingTableFilters(filters);
              setTestingTablePagination({
                ...pagination,
                ...(extra.action === 'paginate' && pagination.pageSize !== testingTablePagination.pageSize
                  ? { current: 1 }
                  : {}),
              });
            }}
            columns={columns}
          />
        </div>
      )}
      <ReservedUsersModal
        isModalOpen={isReservedUsersModalOpen}
        handleCancel={() => setIsReservedUsersModalOpen(false)}
      />
      <AttachTestModal
        isModalOpen={isAttachTestModalOpen}
        handleCancel={() => setIsAttachTestModalOpen(false)}
        schoolId={schoolId}
      />
      {itemToOperate && (
        <ModerateParticipantModal
          key={itemToOperate.user.email}
          itemToOperate={itemToOperate}
          isModalOpen={isModerateParticipantModalOpen}
          handleCancel={() => setIsModerateParticipantModalOpen(false)}
        />
      )}
      <ConfirmModal
        isModalOpen={isSendReminderLettersModalOpen}
        handleCancel={() => setIsSendReminderLettersModalOpen(false)}
        handleConfirm={remindToNotStarted}
        title="Отправить напоминания"
        description="Вы уверены, что хотите отправить напоминания неприступившим пользователям?"
        shouldDisableElement={areReminderLettersSending}
      />
      <ConfirmModal
        isModalOpen={isSendDecisionsModalOpen}
        handleCancel={() => setIsSendDecisionsModalOpen(false)}
        handleConfirm={sendDecisionsLetters}
        title="Отправить финальные уведомления"
        description={`Вы уверены, что хотите отправить уведомления о финальном решении пользователям?
          ${'Важно'.toUpperCase()}! Данное действие можно выполнить только один раз`}
        shouldDisableElement={areDecisionsSending}
      />
      <ConfirmModal
        isModalOpen={isSendTestResultModalOpen}
        handleCancel={() => setIsSendTestResultModalOpen(false)}
        handleConfirm={sendTestResultsCb}
        title="Отправить результаты теста"
        description={`Вы уверены что хотите отправить результаты теста пользователям? ${'Важно'.toUpperCase()}! Данное действие можно выполнить только один раз`}
        shouldDisableElement={areTestResultsSending}
      />
    </FetchDataWrapper>
  );
};
